/*
 * Copyright (c) 2021 - 2026 All rights reserved by Ferenc Farkas <wolferi93@gmail.com>
 */

/**
 * @file driverApi.js
 * @description
 * Networking layer of the mobile **Sofőr / Futár (Driver / Courier)** app mode.
 * Every path is a real `/api/driver/*` route of the backend's driver module;
 * nothing is mocked and no endpoint is invented.
 *
 * Conventions (same as {@link module:helpers/procurementApi}):
 *  - The driver controllers answer `{success, message, data}`, so every call is
 *    unwrapped with {@link unwrapData}.
 *  - The store is carried by the `x-restaurant-id` header resolved from
 *    {@link getManagementScope}, so a multi-store user who picked store B never
 *    gets store A's van stock / routes back.
 *  - Failures reject with `Error('API <status>: <body>')`; the machine codes the
 *    driver module defines (`MODULE_DISABLED`, `NO_VAN_WAREHOUSE`,
 *    `INSUFFICIENT_VAN_STOCK`, `CONTACT_LOCKED`, `CONTACT_REQUIRED`, …) are read
 *    with {@link driverErrorCode} / {@link driverErrorData}.
 *  - There is deliberately NO delete call for business records: drivers can
 *    never remove customers, orders or routes, only create leads/customers,
 *    request changes and pin a party's exact map location
 *    ({@link saveDriverPartyLocation}). The single exception is their OWN
 *    rest-break log entry ({@link discardDriverBreak}) — a driver's personal
 *    time record.
 *  - The fuel-card PIN ({@link revealFuelCardPin}) is returned once, never cached
 *    (a POST), and must never be stored or logged by the caller.
 *
 * @module helpers/driverApi
 */

import {apiFetch} from './WorkSheetContext';
import {getManagementScope} from './managementApi';
import {unwrapData} from './procurementApi';
import {apiErrorCode, apiErrorPayload, apiErrorStatus} from './apiError';
import {normalizeToday} from './driverDailyCheck';
import {pinToPayload} from './geoPin';

/** Machine codes the driver endpoints answer with. */
export const DRIVER_ERROR_CODES = {
    MODULE_DISABLED: 'MODULE_DISABLED',
    NO_VAN_WAREHOUSE: 'NO_VAN_WAREHOUSE',
    INSUFFICIENT_VAN_STOCK: 'INSUFFICIENT_VAN_STOCK',
    CONTACT_LOCKED: 'CONTACT_LOCKED',
    CONTACT_REQUIRED: 'CONTACT_REQUIRED',
    INVALID_CONTACT: 'INVALID_CONTACT',
    INVOICING_NOT_CONFIGURED: 'INVOICING_NOT_CONFIGURED',
    ROUTE_NOT_ACTIVE: 'ROUTE_NOT_ACTIVE',
    TRACKING_DISABLED: 'TRACKING_DISABLED',
    NO_VEHICLE: 'NO_VEHICLE',
    ODOMETER_DECREASED: 'ODOMETER_DECREASED',
    CARD_NOT_ACTIVE: 'CARD_NOT_ACTIVE',
    NO_PIN: 'NO_PIN',
    PIN_UNREADABLE: 'PIN_UNREADABLE',
    TOO_MANY_REVEALS: 'TOO_MANY_REVEALS',
    BREAK_ALREADY_RUNNING: 'BREAK_ALREADY_RUNNING',
    BREAK_OVERLAP: 'BREAK_OVERLAP',
    BREAK_NOT_RUNNING: 'BREAK_NOT_RUNNING',
    CHECK_ALREADY_SUBMITTED: 'CHECK_ALREADY_SUBMITTED',
    INVALID_ANSWERS: 'INVALID_ANSWERS',
    INVALID_PHOTO: 'INVALID_PHOTO',
    TOO_MANY_PHOTOS: 'TOO_MANY_PHOTOS',
};

/**
 * Build a query string from a params object, dropping empty values.
 * @param {object} [params] Query params.
 * @returns {string} `?a=1&b=2`, or `''` when nothing survives.
 */
export const qs = (params = {}) => {
    const parts = Object.entries(params || {})
        .filter(([, value]) => value !== undefined && value !== null && value !== '')
        .map(([key, value]) => `${encodeURIComponent(key)}=${encodeURIComponent(String(value))}`);

    return parts.length ? `?${parts.join('&')}` : '';
};

/**
 * Coerce an unwrapped payload into an array.
 * @param {*} value Unwrapped payload.
 * @param {string[]} [keys] Object keys to try.
 * @returns {Array} Always an array.
 */
const asList = (value, keys = ['items', 'results', 'routes', 'orders']) => {
    if (Array.isArray(value)) {
        return value;
    }
    if (value && typeof value === 'object') {
        for (const key of keys) {
            if (Array.isArray(value[key])) {
                return value[key];
            }
        }
    }

    return [];
};

/**
 * Scope header for a call (the active store).
 * @returns {Promise<Record<string, string>>} Headers to merge.
 */
export const scopeHeaders = async () => {
    let scope = null;

    try {
        scope = await getManagementScope();
    } catch (_error) {
        scope = null;
    }

    return scope?.restaurantId ? {'x-restaurant-id': String(scope.restaurantId)} : {};
};

/**
 * GET a driver route and unwrap its envelope.
 * @param {string} path Path after `/api`.
 * @returns {Promise<*>} Unwrapped payload.
 */
const get = async (path) => unwrapData(await apiFetch(path, {headers: await scopeHeaders()}));

/**
 * GET that bypasses the 10 s stale-while-revalidate response cache — for live
 * state (the running break, GPS-dependent station lookups, the odometer).
 * @param {string} path Path after `/api`.
 * @returns {Promise<*>} Unwrapped payload.
 */
const getFresh = async (path) => unwrapData(await apiFetch(path, {headers: await scopeHeaders(), skipCache: true}));

/**
 * Send a body-carrying request and unwrap its envelope.
 * @param {string} method HTTP method.
 * @param {string} path Path after `/api`.
 * @param {object|null} [body] JSON body (omitted when null/undefined).
 * @returns {Promise<*>} Unwrapped payload.
 */
const send = async (method, path, body) => unwrapData(await apiFetch(path, {
    method,
    headers: {'Content-Type': 'application/json', ...(await scopeHeaders())},
    ...(body === undefined || body === null ? {} : {body: JSON.stringify(body)}),
}));

/* -------------------------------------------------------------------------- */
/*  Error helpers                                                              */
/* -------------------------------------------------------------------------- */

/**
 * Machine code of a failed driver call (`''` when none).
 * @param {Error} error Rejected error.
 * @returns {string} The code.
 */
export const driverErrorCode = (error) => apiErrorCode(error);

/**
 * Structured detail of a failed driver call — `{shortages}` of a 422
 * INSUFFICIENT_VAN_STOCK, `{current}` of a 409 CONTACT_LOCKED. The backend may
 * nest it under `data` or answer it flat; both are read.
 * @param {Error} error Rejected error.
 * @returns {object} The detail object (empty when none).
 */
export const driverErrorData = (error) => {
    const payload = apiErrorPayload(error);

    if (!payload) {
        return {};
    }

    const nested = payload.data && typeof payload.data === 'object' ? payload.data : {};

    return {...payload, ...nested};
};

/**
 * Whether the store has the driver module switched off (403 MODULE_DISABLED).
 * @param {Error} error Rejected error.
 * @returns {boolean} True when the module is disabled.
 */
export const isModuleDisabledError = (error) => (
    driverErrorCode(error) === DRIVER_ERROR_CODES.MODULE_DISABLED
);

/**
 * Whether the van has no warehouse assigned (404 NO_VAN_WAREHOUSE).
 * @param {Error} error Rejected error.
 * @returns {boolean} True when no van warehouse is assigned.
 */
export const isNoVanWarehouseError = (error) => (
    driverErrorCode(error) === DRIVER_ERROR_CODES.NO_VAN_WAREHOUSE
    || (apiErrorStatus(error) === 404 && /NO_VAN_WAREHOUSE/.test(String(error?.message || '')))
);

/* -------------------------------------------------------------------------- */
/*  Profile                                                                    */
/* -------------------------------------------------------------------------- */

/**
 * The driver's own context: store, vehicle, van warehouse and permissions.
 * @returns {Promise<object>} `{user, restaurantId, moduleEnabled, vehicle, warehouse, permissions}`.
 */
export const fetchDriverMe = async () => get('/driver/me');

/* -------------------------------------------------------------------------- */
/*  Routes                                                                     */
/* -------------------------------------------------------------------------- */

/**
 * Route summaries of a date window.
 * @param {{from?: string, to?: string}} [range] `YYYY-MM-DD` bounds.
 * @returns {Promise<Array>} RouteSummary[].
 */
export const fetchDriverRoutes = async ({from, to} = {}) => asList(await get(`/driver/routes${qs({from, to})}`));

/**
 * One route with its stops, polyline and bounds.
 * @param {string} routeId Route plan id.
 * @returns {Promise<object>} RouteDetail.
 */
export const fetchDriverRoute = async (routeId) => get(`/driver/routes/${encodeURIComponent(routeId)}`);

/**
 * Start executing a route.
 * @param {string} routeId Route plan id.
 * @returns {Promise<object>} The updated route.
 */
export const startDriverRoute = async (routeId) => send('POST', `/driver/routes/${encodeURIComponent(routeId)}/start`, {});

/**
 * Finish a route.
 * @param {string} routeId Route plan id.
 * @param {{note?: string}} [body] Optional closing note.
 * @returns {Promise<object>} The updated route.
 */
export const finishDriverRoute = async (routeId, {note} = {}) => send(
    'POST',
    `/driver/routes/${encodeURIComponent(routeId)}/finish`,
    note ? {note} : {},
);

/**
 * Update the visit status of one stop.
 * @param {string} routeId Route plan id.
 * @param {string} stopId Stop id.
 * @param {{visitStatus: string, note?: string}} body New status (+ note).
 * @returns {Promise<object>} DriverStop.
 */
export const updateDriverStop = async (routeId, stopId, {visitStatus, note} = {}) => send(
    'PATCH',
    `/driver/routes/${encodeURIComponent(routeId)}/stops/${encodeURIComponent(stopId)}`,
    {visitStatus, ...(note ? {note} : {})},
);

/**
 * Upload a batch of GPS points of an active route (max 200 per call). Rejects
 * 409 ROUTE_NOT_ACTIVE when the route is not in progress and 403
 * TRACKING_DISABLED when the store switched tracking off — both mean "stop".
 * @param {string} routeId Route plan id.
 * @param {Array<{lat: number, lng: number, accuracy?: number, speed?: number, heading?: number, altitude?: number, recordedAt: string}>} points Points.
 * @returns {Promise<{accepted: number, settings: ?object}>} Server answer.
 */
export const postRouteLocations = async (routeId, points) => send(
    'POST',
    `/driver/routes/${encodeURIComponent(routeId)}/locations`,
    {points},
);

/**
 * Store-level driver tracking settings.
 * @returns {Promise<{locationRetentionDays: number, trackingIntervalSeconds: number, trackingDistanceMeters: number, arrivalRadiusMeters: number, trackingEnabled: boolean}>} Settings.
 */
export const fetchDriverSettings = async () => get('/driver/settings');

/* -------------------------------------------------------------------------- */
/*  Van stock                                                                  */
/* -------------------------------------------------------------------------- */

/**
 * The van warehouse stock. Rejects 404 NO_VAN_WAREHOUSE when none is assigned.
 * @param {{search?: string, all?: boolean}} [params] Filters (`all` includes zero-qty lines).
 * @returns {Promise<{warehouse: ?object, items: Array}>} Stock payload.
 */
export const fetchDriverStock = async ({search, all} = {}) => {
    const data = await get(`/driver/stock${qs({search, all: all ? 'true' : undefined})}`);

    return {
        warehouse: data?.warehouse || null,
        items: asList(data),
    };
};

/**
 * Write goods off the van (Selejtezés) — the van is resolved server-side.
 * @param {{items: Array<{foodId: string, size?: string, qty: number}>, wasteType?: string, comment?: string}} payload Write-off.
 * @returns {Promise<object>} `{_id, receiptType, wasteType, warehouse, lines}`.
 */
export const writeOffDriverStock = async (payload) => send('POST', '/driver/stock/waste', payload);

/* -------------------------------------------------------------------------- */
/*  Sales & orders                                                             */
/* -------------------------------------------------------------------------- */

/**
 * Create a sale from van stock. `generatedId` makes it idempotent.
 * @param {object} payload `{generatedId, party, routePlanId, stopId, items, paymentType, comment, close, needInvoice}`.
 * @returns {Promise<object>} DriverOrder.
 */
export const createDriverSale = async (payload) => send('POST', '/driver/sales', payload);

/**
 * The driver's orders.
 * @param {{from?: string, to?: string, status?: string, routePlanId?: string}} [params] Filters.
 * @returns {Promise<Array>} DriverOrder[].
 */
export const fetchDriverOrders = async ({from, to, status, routePlanId} = {}) => asList(
    await get(`/driver/orders${qs({from, to, status, routePlanId})}`),
);

/**
 * One order.
 * @param {string} orderId Order id.
 * @returns {Promise<object>} DriverOrder.
 */
export const fetchDriverOrder = async (orderId) => get(`/driver/orders/${encodeURIComponent(orderId)}`);

/**
 * Hand an order over to the customer.
 * @param {string} orderId Order id.
 * @param {{paymentType?: string, note?: string, recipientName?: string}} [body] Handover details.
 * @returns {Promise<object>} DriverOrder.
 */
export const handoverDriverOrder = async (orderId, body = {}) => send(
    'POST',
    `/driver/orders/${encodeURIComponent(orderId)}/handover`,
    body,
);

/**
 * Send the order's invoice by e-mail or SMS.
 * @param {string} orderId Order id.
 * @param {{channel: 'email'|'sms', to?: string}} body Channel and (when the party has none) the address/number.
 * @returns {Promise<object>} DriverOrder (with `invoice.sentTo`).
 */
export const sendDriverInvoice = async (orderId, {channel, to} = {}) => send(
    'POST',
    `/driver/orders/${encodeURIComponent(orderId)}/invoice`,
    {channel, ...(to ? {to} : {})},
);

/* -------------------------------------------------------------------------- */
/*  Parties (customers & leads)                                                */
/* -------------------------------------------------------------------------- */

/**
 * Search the customers / leads the driver may see.
 * @param {{search?: string, type?: string, limit?: number}} [params] Filters.
 * @returns {Promise<Array>} Party[].
 */
export const fetchDriverParties = async ({search, type, limit} = {}) => asList(
    await get(`/driver/parties${qs({search, type, limit})}`),
    ['items', 'results', 'parties'],
);

/**
 * One party with its recent orders and open change requests.
 * @param {string} type `customer` | `unifiedCustomer` | `lead`.
 * @param {string} id Party id.
 * @returns {Promise<object>} Party + `{recentOrders, openChangeRequests}`.
 */
export const fetchDriverParty = async (type, id) => get(
    `/driver/parties/${encodeURIComponent(type)}/${encodeURIComponent(id)}`,
);

/**
 * Create a lead.
 * @param {object} body `{name, company?, email?, phone?, address?, city?, postalCode?, note?}`.
 * @returns {Promise<object>} The created party.
 */
export const createDriverLead = async (body) => send('POST', '/driver/leads', body);

/**
 * Create a customer.
 * @param {object} body `{name, company?, email?, phone?, taxNumber?, address{address1,city,postcode,country}}`.
 * @returns {Promise<object>} The created party.
 */
export const createDriverCustomer = async (body) => send('POST', '/driver/customers', body);

/**
 * Ask the back office to change a party's data (the driver can never edit it).
 * @param {{partyType: string, partyId: string, changes: object, note?: string}} body Request.
 * @returns {Promise<object>} ChangeRequest.
 */
export const createDriverChangeRequest = async (body) => send('POST', '/driver/change-requests', body);

/**
 * Pin a customer's / lead's exact location (`PUT /driver/parties/{type}/{id}/location`).
 * Unlike every other party field this is written straight to the CRM record (no
 * change request): the server stamps the pin as a `driver` pin and answers the
 * refreshed party. An invalid pin never reaches the network — it rejects like the
 * backend would (422 `INVALID_LOCATION`).
 * @param {string} type `customer` | `unifiedCustomer` | `lead`.
 * @param {string} id Party id.
 * @param {{lat: number, lng: number, accuracy?: ?number}} pin The pinned point.
 * @returns {Promise<object>} The refreshed party (same shape as {@link fetchDriverParty}).
 */
export const saveDriverPartyLocation = async (type, id, pin) => {
    const payload = pinToPayload(pin);

    if (!payload) {
        throw Object.assign(new Error('API 422: invalid coordinates'), {status: 422, code: 'INVALID_LOCATION'});
    }

    return send('PUT', `/driver/parties/${encodeURIComponent(type)}/${encodeURIComponent(id)}/location`, payload);
};

/**
 * Resolve an address to coordinates (`POST /driver/geocode`) to seed the pin
 * picker. A miss is not an error — the driver drags the marker — so 404 (not
 * found), 503 (no geocoder) and network failures all resolve `null`.
 * @param {{address?: string, zip?: string, city?: string, country?: string}} query One-line `address`, or the structured parts.
 * @returns {Promise<?{lat: number, lng: number, formattedAddress: string, precision: string}>} The match, `null` when none.
 */
export const geocodeDriverAddress = async (query) => {
    try {
        const hit = await send('POST', '/driver/geocode', query || {});

        return Number.isFinite(Number(hit?.lat)) && Number.isFinite(Number(hit?.lng)) ? hit : null;
    } catch (_error) {
        return null;
    }
};

/**
 * Locale key explaining a failed pin save (`''` when the caller should show the
 * server's own sentence). A switched-off module and a missing permission both
 * answer 403; an order-derived guest is read-only (409 `PARTY_READ_ONLY`).
 * @param {Error} error Rejected error.
 * @returns {string} Locale key or `''`.
 */
export const partyPinErrorKey = (error) => {
    const status = apiErrorStatus(error);
    const code = driverErrorCode(error);

    if (status === 403 || isModuleDisabledError(error)) {
        return 'driver_party_pin_forbidden';
    }
    if (status === 409 || code === 'PARTY_READ_ONLY') {
        return 'driver_party_pin_readonly';
    }
    if (status === 422 || code === 'INVALID_LOCATION') {
        return 'driver_party_pin_invalid';
    }

    return status === 404 ? 'driver_party_not_found' : '';
};

/**
 * The driver's own change requests.
 * @param {{status?: string}} [params] Filter.
 * @returns {Promise<Array>} ChangeRequest[].
 */
export const fetchDriverChangeRequests = async ({status} = {}) => asList(
    await get(`/driver/change-requests${qs({status})}`),
    ['items', 'results', 'requests'],
);

/**
 * Colleague directory for the driver (route owner first).
 * @returns {Promise<{self: {hasVoip: boolean, voipExtension: ?string},
 *   contacts: Array<{userId: string, name: string, roleLabel: string, mobile: ?string,
 *   voipExtension: ?string, hasVoip: boolean, isRouteOwner: boolean}>}>} Contacts payload.
 */
export const fetchDriverContacts = async () => {
    const payload = await get('/driver/contacts');

    return {
        self: payload?.self || {hasVoip: false, voipExtension: null},
        contacts: Array.isArray(payload?.contacts) ? payload.contacts : [],
    };
};

/* -------------------------------------------------------------------------- */
/*  Fuel: stations, cards, odometer, refuels                                   */
/* -------------------------------------------------------------------------- */

/**
 * Fuel-station brand catalogue (`mol`, `shell`, … `other`).
 * @returns {Promise<Array<{key: string, label: string}>>} Brands.
 */
export const fetchFuelBrands = async () => asList(await get('/driver/fuel/brands'));

/**
 * Fuel stations around a position, distance-sorted (max 10).
 * @param {{lat: number, lng: number, radius?: number, brandKeys?: (string[]|string)}} params Position, search radius in
 *   metres and an optional brand filter (only stations of these catalog brands are returned).
 * @returns {Promise<{source: 'google'|'osm'|'none', degraded: boolean, suggestedBrandKey: ?string,
 *   stations: Array<{name: string, brandKey: ?string, brandLabel: string, address: string,
 *   lat: number, lng: number, distanceM: number}>}>} Lookup result.
 */
export const fetchNearbyFuelStations = async ({lat, lng, radius, brandKeys} = {}) => {
    const brands = Array.isArray(brandKeys) ? brandKeys.join(',') : brandKeys;
    const data = await getFresh(`/driver/fuel-stations/nearby${qs({lat, lng, radius, brandKeys: brands})}`);

    return {
        source: data?.source || 'none',
        degraded: !!data?.degraded,
        suggestedBrandKey: data?.suggestedBrandKey || null,
        stations: asList(data?.stations, ['stations', 'items']),
    };
};

/**
 * Fuel cards reachable by the driver (own vehicle's + assigned to them).
 * @returns {Promise<Array<{id: string, providerName: string, cardNumberMasked: string, hasPin: boolean,
 *   status: 'active'|'blocked'|'expired', validTo: ?string, vehicleId: ?string, vehiclePlate: ?string,
 *   assignedToMe: boolean, brandKeys: string[]}>>} Cards (the PIN is never part of the list).
 */
export const fetchFuelCards = async () => asList(await getFresh('/driver/fuel-cards'));

/**
 * Reveal a fuel card's PIN. The caller MUST have passed an OS biometric / device
 * passcode gate first and MUST NOT persist or log the answer. Rejects 404, 409
 * CARD_NOT_ACTIVE / NO_PIN / PIN_UNREADABLE and 429 TOO_MANY_REVEALS
 * (`driverErrorData(e).retryAfterSeconds`).
 * @param {string} cardId Fuel-card id.
 * @param {'biometric'|'passcode'|'unknown'} authMethod How the driver unlocked the device prompt.
 * @param {?object} [gpsSnapshot] Safety snapshot ({@link module:helpers/driverGpsSnapshot}); stored in the audit log, omitted when absent.
 * @returns {Promise<{pin: string, expiresAt: string, visibleSeconds: number}>} The one-time reveal.
 */
export const revealFuelCardPin = async (cardId, authMethod = 'unknown', gpsSnapshot = null) => send(
    'POST',
    `/driver/fuel-cards/${encodeURIComponent(cardId)}/reveal-pin`,
    gpsSnapshot ? {authMethod, gpsSnapshot} : {authMethod},
);

/**
 * The driver's vehicle and its last known odometer. Rejects 409 NO_VEHICLE.
 * @returns {Promise<{vehicleId: ?string, vehiclePlate: ?string, lastKnownKm: ?number, lastReadingAt: ?string}>} Odometer.
 */
export const fetchDriverOdometer = async () => getFresh('/driver/vehicle/odometer');

/**
 * Record a refuel. Rejects 409 NO_VEHICLE, 422 ODOMETER_DECREASED
 * (`driverErrorData(e).lastKnownKm`) and 422 validation.
 * @param {{stationBrandKey: string, stationName?: string, odometerKm: number,
 *   location: {lat?: number, lng?: number, address?: string}, refueledAt?: string, notes?: string,
 *   fuelCardId?: string, quantity?: number, grossAmount?: number, fuelType?: string,
 *   odometerPhotoDocumentId?: string, gpsSnapshot?: object}} body Refuel (`gpsSnapshot` never causes a 4xx;
 *   the response carries the scored `gpsSnapshot` projection `{status, lat, lng, accuracyM, capturedAt, riskLevel, flags}`).
 * @returns {Promise<object>} The created DriverRefuel (`{item}` is unwrapped).
 */
export const createDriverRefuel = async (body) => {
    const data = await send('POST', '/driver/refuels', body);

    return data?.item || data;
};

/**
 * The driver's refuels, newest first.
 * @param {{from?: string, to?: string, limit?: number}} [params] Filters.
 * @returns {Promise<Array<object>>} DriverRefuel[] (each with `gpsSnapshot`, `null` for legacy rows).
 */
export const fetchDriverRefuels = async ({from, to, limit} = {}) => asList(
    await getFresh(`/driver/refuels${qs({from, to, limit})}`),
);

/* -------------------------------------------------------------------------- */
/*  Munkaidő (clock in / out and the driver's own work time)                   */
/* -------------------------------------------------------------------------- */

/**
 * Clock state: the open shift (or null) plus today's / this month's minutes. Rejects 422 EMPLOYEE_NOT_LINKED.
 * @returns {Promise<{open: ?object, stale: boolean, todayMinutes: number, monthMinutes: number}>} State (`open` is a DriverWorkEntry).
 */
export const fetchDriverWorkTimeCurrent = async () => {
    const data = await getFresh('/driver/work-time/current');

    return {
        open: data?.open || null,
        stale: !!data?.stale,
        todayMinutes: Number(data?.todayMinutes) || 0,
        monthMinutes: Number(data?.monthMinutes) || 0,
    };
};

/**
 * Open a shift. Rejects 409 ALREADY_CLOCKED_IN (`driverErrorData(e).openEntry`/`stale`) / DAY_ALREADY_RECORDED /
 * MONTH_CLOSED and 422 EMPLOYEE_NOT_LINKED / PLACE_REQUIRED (`driverErrorData(e).places`) / UNKNOWN_PLACE.
 * @param {{placeId?: string, note?: string, location?: {lat: number, lng: number}}} [body] Clock-in data.
 * @returns {Promise<object>} The open DriverWorkEntry.
 */
export const driverClockIn = async (body = {}) => {
    const data = await send('POST', '/driver/work-time/clock-in', body);

    return data?.item || data;
};

/**
 * Close the open shift. `endAt` only to close a forgotten shift. Rejects 409 NOT_CLOCKED_IN / DAY_ALREADY_RECORDED /
 * MONTH_CLOSED and 422 OPEN_ENTRY_TOO_OLD / INVALID_RANGE / SHIFT_TOO_LONG / FUTURE_DATE.
 * @param {{note?: string, location?: object, endAt?: string}} [body] Clock-out data.
 * @returns {Promise<{item: ?object, items: object[], discarded: boolean, minutes: number}>} Closed rows.
 */
export const driverClockOut = async (body = {}) => {
    const data = await send('POST', '/driver/work-time/clock-out', body);

    return {
        item: data?.item || null,
        items: asList(data, ['items']),
        discarded: !!data?.discarded,
        minutes: Number(data?.minutes) || 0,
    };
};

/**
 * The driver's own work time of a month. Rejects 422 INVALID_MONTH / EMPLOYEE_NOT_LINKED.
 * @param {{month?: string}} [params] `YYYY-MM` (default: the current Budapest month).
 * @returns {Promise<{month: string, items: object[], summary: {totalMinutes: number, daysWorked: number, perSite: object[]}, open: ?object}>} Month.
 */
export const fetchDriverWorkTime = async ({month} = {}) => {
    const data = await getFresh(`/driver/work-time${qs({month})}`);

    return {
        month: data?.month || month || '',
        items: asList(data, ['items']),
        summary: {
            totalMinutes: Number(data?.summary?.totalMinutes) || 0,
            daysWorked: Number(data?.summary?.daysWorked) || 0,
            perSite: asList(data?.summary?.perSite, []),
        },
        open: data?.open || null,
    };
};

/* -------------------------------------------------------------------------- */
/*  Saját statisztika                                                          */
/* -------------------------------------------------------------------------- */

/**
 * The driver's own month statistics (`GET /api/driver/stats/summary`). Rejects
 * 422 `INVALID_MONTH`, 400 (no store), 403 (no permission / module disabled).
 * `distanceKm` is `null` with fewer than two odometer readings or no vehicle and
 * can include other drivers' km on a shared vehicle.
 * @param {{month?: string, restaurantId?: string}} [params] `month` is `YYYY-MM` (default: the current Budapest month).
 * @returns {Promise<{month: string,
 *   refuels: {count: number, liters: number, grossAmount: number, currency: string, incompleteCount: number, flaggedCount: number},
 *   breaks: {totalMinutes: number, plannedMinutes: number, unexpectedMinutes: number, byCategory: Object<string, number>},
 *   distanceKm: ?number, daily: Array<{day: string, refuelLiters: number, breakMinutes: number}>}>} Summary.
 */
export const fetchDriverStatsSummary = async ({month, restaurantId} = {}) => getFresh(
    `/driver/stats/summary${qs({month, restaurantId})}`,
);

/* -------------------------------------------------------------------------- */
/*  Breaks (pihenők és szünetek)                                               */
/* -------------------------------------------------------------------------- */

/**
 * Own breaks of a day range (≤ 31 days, default today) plus totals.
 * @param {{from?: string, to?: string}} [range] `YYYY-MM-DD` bounds.
 * @returns {Promise<{running: ?object, items: Array<object>, totals: {byCategory: object,
 *   totalMinutes: number, days: Array<object>}}>} Breaks payload.
 */
export const fetchDriverBreaks = async ({from, to} = {}) => {
    const data = await getFresh(`/driver/breaks${qs({from, to})}`);

    return {
        running: data?.running || null,
        items: asList(data?.items, ['items', 'breaks']),
        totals: data?.totals || {byCategory: {}, totalMinutes: 0, days: []},
    };
};

/**
 * Start (no `endAt`) or log (with `endAt`) a break. Rejects 409
 * BREAK_ALREADY_RUNNING / BREAK_OVERLAP and 422 (max 24 h per entry).
 * @param {{category: string, planned: boolean, startAt?: string, endAt?: string,
 *   startLocation?: {lat: number, lng: number}, notes?: string, stopRunning?: boolean}} body Break.
 * @returns {Promise<object>} The created DriverBreak.
 */
export const createDriverBreak = async (body) => {
    const data = await send('POST', '/driver/breaks', body);

    return data?.item || data;
};

/**
 * Edit a break.
 * @param {string} breakId Break id.
 * @param {{category?: string, planned?: boolean, startAt?: string, endAt?: string, notes?: string}} body Changes.
 * @returns {Promise<object>} The updated DriverBreak.
 */
export const updateDriverBreak = async (breakId, body) => {
    const data = await send('PATCH', `/driver/breaks/${encodeURIComponent(breakId)}`, body);

    return data?.item || data;
};

/**
 * Stop the running break. Rejects 409 BREAK_NOT_RUNNING.
 * @param {string} breakId Break id.
 * @param {{endAt?: string, endLocation?: {lat: number, lng: number}}} [body] Optional end time / place.
 * @returns {Promise<object>} The finished DriverBreak.
 */
export const stopDriverBreak = async (breakId, body = {}) => {
    const data = await send('POST', `/driver/breaks/${encodeURIComponent(breakId)}/stop`, body);

    return data?.item || data;
};

/**
 * Delete one of the driver's OWN break entries.
 * @param {string} breakId Break id.
 * @returns {Promise<*>} Server answer.
 */
export const discardDriverBreak = async (breakId) => send('DELETE', `/driver/breaks/${encodeURIComponent(breakId)}`);

/* -------------------------------------------------------------------------- */
/*  Napi ellenőrzőlap (daily pre-shift check)                                  */
/* -------------------------------------------------------------------------- */

/**
 * Today's check sheet: the server template (sections + questions), today's
 * submitted check (or null) and the assigned vehicle.
 * @returns {Promise<{date: string, template: {sections: Array<object>, questions: Array<object>},
 *   check: ?object, vehicle: ?{_id: string, make: ?string, model: ?string, plateNumber: ?string}}>} Today payload.
 */
export const fetchDriverDailyCheckToday = async () => normalizeToday(await getFresh('/driver/daily-checks/today'));

/**
 * Submit today's check. Rejects 409 CHECK_ALREADY_SUBMITTED (`driverErrorData(e).checkId`),
 * 422 INVALID_ANSWERS (`driverErrorData(e).fields`), INVALID_PHOTO, TOO_MANY_PHOTOS.
 * @param {{answers: Object<string, (boolean|string)>, photos?: Array<object>, location?: {lat: number, lng: number}}} body Check.
 * @returns {Promise<object>} The created DriverDailyCheck (`{item}` is unwrapped; `plateMatch === false` flags a plate mismatch).
 */
export const submitDriverDailyCheck = async (body) => {
    const data = await send('POST', '/driver/daily-checks', body);

    return data?.item || data;
};

/**
 * Attach more photos to a submitted check.
 * @param {string} checkId Check id.
 * @param {Array<{documentId: string, area: string, severity: string, note: string, takenAt: string}>} photos Photos.
 * @returns {Promise<object>} The updated DriverDailyCheck.
 */
export const addDriverDailyCheckPhotos = async (checkId, photos) => {
    const data = await send('POST', `/driver/daily-checks/${encodeURIComponent(checkId)}/photos`, {photos});

    return data?.item || data;
};

/**
 * The driver's checks in a range, newest first.
 * @param {{from?: string, to?: string}} [range] `YYYY-MM-DD` bounds.
 * @returns {Promise<Array<object>>} DriverDailyCheck[].
 */
export const fetchDriverDailyChecks = async ({from, to} = {}) => asList(
    await getFresh(`/driver/daily-checks${qs({from, to})}`),
);

export default {
    fetchDriverMe,
    fetchDriverContacts,
    fetchDriverRoutes,
    fetchDriverRoute,
    startDriverRoute,
    finishDriverRoute,
    updateDriverStop,
    postRouteLocations,
    fetchDriverSettings,
    fetchDriverStock,
    createDriverSale,
    fetchDriverOrders,
    fetchDriverOrder,
    handoverDriverOrder,
    sendDriverInvoice,
    fetchDriverParties,
    fetchDriverParty,
    createDriverLead,
    createDriverCustomer,
    createDriverChangeRequest,
    saveDriverPartyLocation,
    geocodeDriverAddress,
    fetchDriverChangeRequests,
    fetchFuelBrands,
    fetchNearbyFuelStations,
    fetchFuelCards,
    revealFuelCardPin,
    fetchDriverOdometer,
    createDriverRefuel,
    fetchDriverRefuels,
    fetchDriverStatsSummary,
    fetchDriverBreaks,
    createDriverBreak,
    updateDriverBreak,
    stopDriverBreak,
    discardDriverBreak,
    fetchDriverDailyCheckToday,
    submitDriverDailyCheck,
    addDriverDailyCheckPhotos,
    fetchDriverDailyChecks,
    fetchDriverWorkTimeCurrent,
    driverClockIn,
    driverClockOut,
    fetchDriverWorkTime,
};

/* -------------------------------------------------------------------------- */
/*  Shared reference data (/api/reference/*)                                   */
/* -------------------------------------------------------------------------- */

/**
 * Country names for the new-customer form (shared reference endpoint — never a
 * hardcoded list).
 * @param {string} [search] Substring filter.
 * @returns {Promise<string[]>} Country names.
 */
export const fetchReferenceCountries = async (search) => asList(
    await get(`/reference/countries${qs({search})}`),
    ['countries', 'items'],
);

/**
 * Hungarian ZIP lookup (`zip` → place + county) for address autofill.
 * @param {string} zip Postal code.
 * @returns {Promise<Array<{zip: string, placeName: string, county: string}>>} Matches.
 */
export const fetchReferenceZip = async (zip) => asList(
    await get(`/reference/zip-codes${qs({zip, limit: 5})}`),
    ['zipCodes', 'items'],
);
